<script setup lang="ts">
import { type FilledMediaContent, MediaContentType } from "~/types/Catalog";

const {
  media: mediaItems,
  activeMediaItemIndex,
  scrollOffsetTop = 0,
} = defineProps<{
  media: FilledMediaContent[];
  activeMediaItemIndex: number;
  scrollOffsetTop?: number;
  imageAltTag: string;
}>();

const emit = defineEmits<{
  (e: "active-image-change", index: number): void;
}>();

const thumbnailItemsRef = useTemplateRefsList<HTMLLIElement>();
const thumbnailItemsContainerRef = useTemplateRef("thumbnailItemsContainerRef");

watchEffect(() => {
  const activeThumbnailRef = thumbnailItemsRef.value?.[activeMediaItemIndex];

  if (activeThumbnailRef) {
    const activeThumbnailLeft = activeThumbnailRef.offsetLeft;
    const activeThumbnailTop = activeThumbnailRef.offsetTop;

    thumbnailItemsContainerRef.value?.scrollTo({
      // 16 = the padding on the side of the thumbnails
      left: activeThumbnailLeft - 16,
      top: activeThumbnailTop - scrollOffsetTop,
      behavior: "smooth",
    });
  }
});
</script>

<template>
  <ul
    ref="thumbnailItemsContainerRef"
    class="static lg:absolute left-0 top-0 h-full max-w-full overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto flex lg:flex-col gap-2 lg:gap-3 z-10"
  >
    <li
      v-for="(mediaItem, index) in mediaItems"
      :ref="thumbnailItemsRef.set"
      :key="mediaItem.url"
    >
      <button
        class="cursor-pointer bg-platform-bg-2 lg:min-w-full lg:w-full aspect-[4/5] min-w-28 max-w-28 flex justify-center items-center transition-colors border overflow-hidden"
        :class="{
          'border-platform-border-2': activeMediaItemIndex === index,
          'border-platform-bg-2': activeMediaItemIndex !== index,
        }"
        :data-test="`product-media-gallery-thumbnail-${index}${activeMediaItemIndex === index ? '-active' : ''}`"
        aria-label="Select media item"
        @click="emit('active-image-change', index)"
        @keydown="emit('active-image-change', index)"
      >
        <div class="overflow-hidden h-full flex items-center justify-center">
          <NuxtImg
            v-if="mediaItem.type === MediaContentType.Image"
            :src="mediaItem.url ?? brokenMediaFallback"
            :alt="imageAltTag"
            data-cy="product-media-gallery-other-media"
            class="object-contain drop-shadow-product-thumbnail"
            provider="imgix"
            preset="productGallery"
          />
        </div>
        <BaseVideoPlayer
          v-if="mediaItem.type === MediaContentType.Video"
          data-cy="product-media-gallery-other-media"
          :autoplay="false"
          :src="mediaItem.url ?? brokenMediaFallback"
        />
      </button>
    </li>
  </ul>
</template>
